import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FaCheckCircle } from 'react-icons/fa';

import { TextInput, SelectInput, useScreenQuery, Button, ErrorMessage } from 'playup-web-core';

import useNotify from '../../../hooks/useNotify';
import EmailVerification from './EmailVerification';
import MobileVerification from './MobileVerification';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { updateCurrentUser } from '../../../api/users';
import ResetPasswordForm from './ResetPasswordForm';
import { AiFillInfoCircle } from 'react-icons/ai';
import ControlledAddressInput from '../../../components/PortalGeoAddress/ControlledAddressInput';

// TODO: This is a bad abstraction, but a good use of type alias.
interface FormData extends PortalUser {
	address?: {
		street_number: string;
		street_name: string;
		street_type: string;
		city: string;
		state: string;
		postcode: string;
	};
	isManualAddress: boolean;
}

const ProfileForm = ({ isSmallScreen, onResetPasswordButtonPress }: Props) => {
	const notify = useNotify();
	const { isSmall } = useScreenQuery();

	const { refreshPortalUser, user } = React.useContext(PortalSessionContext);

	const [showEmailVerification, setShowEmailVerification] = React.useState(false);
	const [showMobileVerification, setShowMobileVerification] = React.useState(false);
	const [sending, setSending] = React.useState(false);
	const [errors, setErrors] = React.useState<any>({});

	const defaultAddress = React.useMemo(() => {
		return {
			unit_number: user?.unit_number ?? '',
			street_number: user?.street_number ?? '',
			street_name: user?.street_name ?? '',
			street_type: user?.street_type ?? '',
			city: user?.city ?? '',
			state: user?.state ?? '',
			postcode: user?.postcode ?? '',
			country: user?.country ?? '',
		};
	}, [
		user?.city,
		user?.country,
		user?.postcode,
		user?.state,
		user?.street_name,
		user?.street_number,
		user?.street_type,
		user?.unit_number,
	]);

	const methods = useForm<FormData>({
		defaultValues: {
			first_name: user?.first_name,
			last_name: user?.last_name,
			email: user?.email,
			name: user?.name,
			birth_date: user?.birth_date,
			phone: user?.phone,
			address: {
				...defaultAddress,
			},
			...defaultAddress,
		},
	});
	React.useEffect(() => {
		methods.reset({
			first_name: user?.first_name,
			last_name: user?.last_name,
			email: user?.email,
			name: user?.name,
			birth_date: user?.birth_date,
			phone: user?.phone,
			address: {
				...defaultAddress,
			},
			...defaultAddress,
		});
	}, [defaultAddress, methods, user]);
	const {
		register,
		watch,
		handleSubmit,
		formState: { dirtyFields },
	} = methods;
	const onSubmit = (data: FormData) => {
		setSending(true);
		let result = { ...data };
		let dirtyFieldsKey = Object.keys(dirtyFields);
		if (!data.isManualAddress) {
			if (dirtyFields?.address) {
				result = { ...data, ...data.address };

				const addressKeys = Object.keys(data.address!);
				dirtyFieldsKey = dirtyFieldsKey.concat(addressKeys);
			}
		}

		dirtyFieldsKey = dirtyFieldsKey.filter((key) => key !== 'address' && key !== 'isManualAddress');
		const userUpdates: Partial<PortalUser> = {};
		for (let index = 0; index < dirtyFieldsKey.length; index++) {
			const element = dirtyFieldsKey[index];
			userUpdates[element] = result[element];
		}
		updateCurrentUser(userUpdates)
			.then(() => {
				refreshPortalUser();
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your Profile has been updated!',
					timeout: 2000,
				});
			})
			.catch((error) => {
				setErrors(error.errors);
			})
			.finally(() => {
				setSending(false);
			});
	};

	return (
		<>
			{showEmailVerification && <EmailVerification onClose={() => setShowEmailVerification(false)} />}
			{showMobileVerification && (
				<MobileVerification
					mobileNumber={user?.phone}
					onClose={() => setShowMobileVerification(false)}
					onRefresh={refreshPortalUser}
				/>
			)}
			<FormProvider {...methods}>
				<AccountInfoForm onSubmit={handleSubmit(onSubmit)}>
					<StyledTextInput
						editable={false}
						isSmallScreen={isSmall}
						disabled
						label="First Name"
						type="text"
						placeholder="Must match your ID"
						error={errors && errors.first_name}
						{...register('first_name')}
					/>
					<StyledTextInput
						editable={false}
						isSmallScreen={isSmall}
						disabled
						label="Last Name"
						type="text"
						placeholder="Must match your ID"
						error={errors && errors.last_name}
						{...register('last_name')}
					/>
					<StyledTextInput
						editable={false}
						isSmallScreen={isSmall}
						disabled
						label="Username"
						type="text"
						placeholder="Username"
						error={errors && errors.name}
						{...register('name')}
					/>
					<StyledTextInput
						editable={!user?.email_verified}
						isSmallScreen={isSmall}
						disabled={user?.email_verified}
						label="Email"
						id="profile_email"
						type="email"
						placeholder="Enter a valid Email"
						error={errors && errors.email}
						rightButton={
							user?.email_verified ? (
								<VerifiedIcon $isSmallScreen={isSmall} />
							) : (
								<AiFillInfoCircle
									color="#CD3024"
									height={isSmallScreen ? '15px' : '24px'}
									width={isSmallScreen ? '15px' : '24px'}
								/>
							)
						}
						{...register('email')}
					>
						{!user?.email_verified && (
							<VerifyButton isSmallScreen={isSmallScreen} type="button" onClick={() => setShowEmailVerification(true)}>
								TAP TO VERIFY
							</VerifyButton>
						)}
					</StyledTextInput>
					<StyledTextInput
						editable={false}
						isSmallScreen={isSmall}
						type="date"
						disabled
						id="profile_birth_date"
						label="Date of Birth"
						error={errors && errors.birth_date}
						{...register('birth_date')}
					/>
					<StyledTextInput
						editable={!user?.phone_verified}
						isSmallScreen={isSmall}
						disabled={user?.phone_verified}
						label="Mobile Number"
						type="text"
						placeholder="Mobile Number"
						error={errors && errors.phone}
						rightButton={
							user?.phone_verified ? (
								<VerifiedIcon $isSmallScreen={isSmall} />
							) : (
								<AiFillInfoCircle
									color="#CD3024"
									height={isSmallScreen ? '15px' : '24px'}
									width={isSmallScreen ? '15px' : '24px'}
								/>
							)
						}
						{...register('phone')}
					>
						{!user?.phone_verified && (
							<VerifyButton isSmallScreen={isSmallScreen} type="button" onClick={() => setShowMobileVerification(true)}>
								TAP TO VERIFY
							</VerifyButton>
						)}
					</StyledTextInput>
					<StyledSelectInput
						editable={false}
						disabled
						isSmallScreen={isSmall}
						label="Country"
						error={errors && errors.country}
						{...register('country')}
					>
						<option value="AU">Australia</option>
						<option value="NZ">New Zealand</option>
					</StyledSelectInput>
					<ControlledAddressInput
						darkMode
						queryPlaceholder={`${watch('unit_number') ? `${watch('unit_number')}/` : ''}${
							watch('street_number') ?? ''
						} ${watch('street_name') ?? ''} ${watch('street_type') ?? ''}, ${watch('city') ?? ''} ${
							watch('state') ?? ''
						}`?.trim()}
					/>
					{/* <PortalGeoAddress
						//Address field can be update all the time
						disabled={false}
						label="Address"
						name="geo_address"
						errors={errors && errors}
						country={watch('country') || 'AU'}
					/> */}
					<StyledButton isSmallScreen={isSmall} type="submit" isLoading={sending}>
						Update Account
					</StyledButton>

					{errors && errors.error && <ErrorMessage>{errors.error}</ErrorMessage>}
				</AccountInfoForm>
				<ResetPasswordForm />
				{/* <StyledButton
					style={{ marginTop: 0 }}
					isSmallScreen={isSmall}
					type="submit"
					onClick={onResetPasswordButtonPress}
				>
					Reset Password
				</StyledButton> */}
			</FormProvider>
		</>
	);
};
export default ProfileForm;

interface Props {
	isSmallScreen: boolean;
	onResetPasswordButtonPress: () => void;
}

const AccountInfoForm = styled.form`
	width: 100%;
`;

const VerifiedIcon = styled(FaCheckCircle)<{ $isSmallScreen: boolean }>`
	height: ${({ $isSmallScreen }) => ($isSmallScreen ? '15px' : '24px')};
	width: ${({ $isSmallScreen }) => ($isSmallScreen ? '15px' : '24px')};
	margin-right: ${({ $isSmallScreen }) => ($isSmallScreen ? '0px' : '5px')};
	color: ${({ theme }) => theme.colors.success};
`;

const VerifyButton = styled.button<{ isSmallScreen }>`
	color: white;
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
	border: 1px solid #b33a3a;
	padding-bottom: 3px;
	padding: 3px;
	font-weight: 600;
	borderradius: 2px;
	background-color: #cd3024;
	&:focus {
		outline: none;
	}
`;

const StyledTextInput = styled(TextInput)<{ isSmallScreen }>(({ isSmallScreen }) => ({
	marginBottom: isSmallScreen ? '15px' : '20px',
}));
const StyledSelectInput = styled(SelectInput)<{ isSmallScreen }>(({ isSmallScreen }) => ({
	marginBottom: isSmallScreen ? '15px' : '20px',
}));

const StyledButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '30px' : '40px')};
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
`;
